import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 8
const MEMORY_SIZE = 0x0400
const ACTIVE_START = 0x033c
const SCAN_START = 0x0338

type P60Spec = {
  id: string
  model: string
  type: string
  summary: string
  channels: 4 | 10 | 32
}

export type KenwoodP60Profile = {
  protocol: 'kenwood-p60'
  id: string
  vendor: 'Kenwood'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 0
  memorySize: number
  types: readonly string[]
  powerLevels: readonly ['Low', 'High']
}

const SPECS: readonly P60Spec[] = [
  { id: 'kenwood-tk-260', model: 'TK-260', type: 'P0260', summary: 'Kenwood TK-260 compact VHF portable.', channels: 4 },
  { id: 'kenwood-tk-270', model: 'TK-270', type: 'P0270', summary: 'Kenwood TK-270 VHF portable.', channels: 32 },
  { id: 'kenwood-tk-272', model: 'TK-272', type: 'P0272', summary: 'Kenwood TK-272 VHF portable.', channels: 10 },
  { id: 'kenwood-tk-278', model: 'TK-278', type: 'P0278', summary: 'Kenwood TK-278 VHF portable.', channels: 32 },
  { id: 'kenwood-tk-360', model: 'TK-360', type: 'P0360', summary: 'Kenwood TK-360 compact UHF portable.', channels: 4 },
  { id: 'kenwood-tk-370', model: 'TK-370', type: 'P0370', summary: 'Kenwood TK-370 UHF portable.', channels: 32 },
  { id: 'kenwood-tk-372', model: 'TK-372', type: 'P0372', summary: 'Kenwood TK-372 UHF portable.', channels: 10 },
  { id: 'kenwood-tk-378', model: 'TK-378', type: 'P0378', summary: 'Kenwood TK-378 UHF portable.', channels: 32 },
]

export const KENWOOD_P60_PROFILES: readonly KenwoodP60Profile[] = SPECS.map(
  (spec) => ({
    protocol: 'kenwood-p60',
    id: spec.id,
    vendor: 'Kenwood',
    model: spec.model,
    summary: spec.summary,
    tags: ['Experimental', 'Read + write', `${spec.channels} channels`, 'No channel names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: spec.channels,
    nameLength: 0,
    memorySize: MEMORY_SIZE,
    types: [spec.type],
    powerLevels: ['Low', 'High'],
  }),
)

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Low',
    skip: false,
  }
}

function active(image: Uint8Array, number: number): boolean {
  const index = number - 1
  return (image[ACTIVE_START + Math.floor(index / 8)] & (1 << (index & 7))) === 0
}

function setActive(image: Uint8Array, number: number, value: boolean): void {
  const index = number - 1
  const offset = ACTIVE_START + Math.floor(index / 8)
  const bit = 1 << (index & 7)
  image[offset] = value ? image[offset] & ~bit : image[offset] | bit
}

function scan(image: Uint8Array, number: number): boolean {
  const index = number - 1
  return (image[SCAN_START + Math.floor(index / 8)] & (1 << (index & 7))) !== 0
}

function setScan(image: Uint8Array, number: number, value: boolean): void {
  const index = number - 1
  const offset = SCAN_START + Math.floor(index / 8)
  const bit = 1 << (index & 7)
  image[offset] = value ? image[offset] | bit : image[offset] & ~bit
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function rawBcdValue(raw: Uint8Array): number | null {
  if (raw.every((value) => value === 0xff)) return null
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    value = value * 100 + (raw[index] >> 4) * 10 + (raw[index] & 0x0f)
  }
  return value
}

function decimalBcd(value: number): Uint8Array {
  const result = new Uint8Array(2)
  for (let index = 0; index < 2; index += 1) {
    const part = value % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  return result
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = rawBcdValue(raw)
  if (value === null || value === 0 || value === 16665) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value >= 12000) return { mode: 'dtcs', ctcss: null, dtcs: value - 12000, polarity: 'R' }
  if (value >= 8000) return { mode: 'dtcs', ctcss: null, dtcs: value - 8000, polarity: 'N' }
  return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): Uint8Array {
  if (channel.toneMode === 'none') return Uint8Array.of(0xff, 0xff)
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    return decimalBcd(Math.round(channel.ctcss * 10))
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const result = decimalBcd(channel.dtcs)
    result[1] |= channel.dtcsPolarity === 'R' ? 0xc0 : 0x80
    return result
  }
  return Uint8Array.of(0xff, 0xff)
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  return tone.mode === 'dtcs' && tone.dtcs === channel.dtcs && tone.polarity === channel.dtcsPolarity
}

export function parseKenwoodP60Channel(
  image: Uint8Array,
  profile: KenwoodP60Profile,
  number: number,
): WebChannel {
  const offset = 0x10 + (number - 1) * 16
  const raw = image.slice(offset, offset + 16)
  if (raw.byteLength !== 16 || raw[0] === 0xff || !active(image, number)) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const txFrequency = raw[4] === 0xff || raw[14] === 0xff ? null : decodeLbcd(raw.slice(4, 8))
  const txTone = decodeTone(raw.slice(10, 12))
  const rxtone = decodeTone(raw.slice(8, 10))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  return {
    number,
    empty: false,
    name: '',
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: 'FM',
    power: raw[12] & 0x04 ? 'High' : 'Low',
    skip: !scan(image, number),
  }
}

export function parseKenwoodP60Channels(
  image: Uint8Array,
  profile: KenwoodP60Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseKenwoodP60Channel(image, profile, index + 1),
  )
}

export function applyKenwoodP60Channel(
  image: Uint8Array,
  profile: KenwoodP60Profile,
  channel: WebChannel,
): void {
  const offset = 0x10 + (channel.number - 1) * 16
  const raw = image.subarray(offset, offset + 16)
  setActive(image, channel.number, !channel.empty)
  setScan(image, channel.number, !channel.empty && !channel.skip)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
    raw[14] = 0
  } else {
    raw.subarray(4, 8).fill(0xff)
    raw[14] = 0xff
  }
  if (!toneMatches(channel, decodeTone(raw.slice(10, 12))) || !toneMatches(channel, decodeTone(raw.slice(8, 10)))) {
    const tone = encodeTone(channel)
    raw.set(tone, 8)
    raw.set(tone, 10)
  }
  raw[12] = channel.power === 'High' ? raw[12] | 0x04 : raw[12] & ~0x04
}

async function readAck(transport: RadioTransport): Promise<void> {
  if ((await transport.readExact(1, 2500))[0] !== ACK) throw new Error('The Kenwood radio rejected the operation.')
}

async function enter(profile: KenwoodP60Profile, transport: RadioTransport): Promise<void> {
  await transport.discardInput()
  await transport.write(new TextEncoder().encode('PROGRAM'))
  await readAck(transport)
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 2500)
  if (!profile.types.some((type) => new TextDecoder().decode(identity).includes(type))) {
    throw new Error(`The radio does not identify as ${profile.model}.`)
  }
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport)
}

export async function readKenwoodP60RadioWithTransport(
  profile: KenwoodP60Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport)
  const image = new Uint8Array(MEMORY_SIZE)
  const total = MEMORY_SIZE / BLOCK_SIZE
  for (let index = 0; index < total; index += 1) {
    const address = index * BLOCK_SIZE
    await transport.write(Uint8Array.of(0x52, (address >> 8) & 0xff, address & 0xff, BLOCK_SIZE))
    image.set((await transport.readExact(12, 3000)).slice(4), address)
    await transport.write(Uint8Array.of(ACK))
    await readAck(transport)
    onProgress?.({ current: index + 1, total, label: 'Reading Kenwood 60-series memory' })
  }
  return { image, reportedModel: profile.model }
}

export async function writeKenwoodP60RadioWithTransport(
  profile: KenwoodP60Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  await enter(profile, transport)
  const total = Math.floor(0x03d0 / BLOCK_SIZE) + 1
  for (let index = 0; index < total; index += 1) {
    const address = index * BLOCK_SIZE
    const frame = new Uint8Array(12)
    frame.set([0x57, (address >> 8) & 0xff, address & 0xff, BLOCK_SIZE])
    frame.set(image.slice(address, address + BLOCK_SIZE), 4)
    await transport.write(frame)
    await readAck(transport)
    onProgress?.({ current: index + 1, total, label: 'Writing Kenwood 60-series memory' })
  }
}
